import React from "react";
import { Link } from "react-router-dom";
import { SERVICES } from "../content";
import usePageMeta from "../hooks/usePageMeta";
import useHashScroll from "../hooks/useHashScroll";
import { Check } from "lucide-react";
import Breadcrumbs from "../components/Breadcrumbs";
import AnswersSection from "../components/AnswersSection";
import TrustSection from "../components/TrustSection";
import QuickQuoteStrip from "../components/QuickQuoteStrip";

export default function Services() {
  useHashScroll();
  usePageMeta({
    title: "Interior Fit-Out Services Dubai | Joinery, Upholstery & Renovation UAE",
    description:
      "We design and deliver turnkey interior fit-out across the UAE — civil work, MEP, joinery, furniture, curtains and finishing, all under one roof. Since 1993.",
    canonical: "https://timefurniture.ae/services",
    ogImage: "/projects/mubarak-villa-1.webp",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: SERVICES.map((s, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Service",
          name: s.title,
          description: s.description,
          provider: { "@id": "https://timefurniture.ae/#organization" },
          areaServed: ["Dubai", "Sharjah", "United Arab Emirates"],
        },
      })),
    },
  });
  return (
    <div data-testid="services-page" className="bg-[#FAF5EC]">
      <Breadcrumbs
        testid="services-breadcrumbs"
        items={[
          { label: "Home", to: "/" },
          { label: "Services" },
        ]}
      />
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 md:pt-14 pb-16">
        <p className="eyebrow mb-6">Services · Complete Turnkey Interior Solutions</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-5xl text-[#1C110C]">
          Ten disciplines,
          <br />
          <em className="text-[#6E1F2C] not-italic">one contract</em>.
        </h1>
        <p className="mt-8 max-w-2xl text-[#3E2A22] leading-relaxed text-lg">
          From demolition to final styling, Time Furniture UAE is the turnkey
          interior contractor clients trust — custom furniture, joinery and
          carpentry in Dubai, plus in-house upholstery for sofas, chairs, beds
          and wall panels — all delivered by our own team.
        </p>
        <div
          data-testid="services-inhouse-banner"
          className="mt-10 inline-flex items-center gap-3 border-l-2 border-[#B8904E] pl-5 py-2 text-[#1C110C]"
        >
          <Check size={16} className="text-[#6E1F2C]" strokeWidth={2.4} />
          <p className="text-sm md:text-base font-medium">
            We don&rsquo;t outsource execution — everything is handled in-house.
          </p>
        </div>
      </section>

      <TrustSection
        testid="services-trust"
        heading="Why clients choose Time Furniture."
      />

      {SERVICES.map((s, idx) => (
        <section
          key={s.key}
          id={s.key}
          data-testid={`service-section-${s.key}`}
          className={`py-28 md:py-40 lg:py-48 ${idx % 2 === 0 ? "bg-[#FAF5EC]" : "bg-white"} border-t border-[#6E1F2C]/15`}
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className={`lg:col-span-6 ${idx % 2 === 0 ? "" : "lg:order-2"}`}>
                <div className="overflow-hidden img-zoom-parent aspect-[4/3] md:aspect-auto md:h-[480px]">
                  <img
                    src={s.image}
                    alt={s.imageAlt || `${s.title} by Time Furniture in Dubai, Abu Dhabi & Sharjah`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover img-zoom"
                  />
                </div>
                {s.imageCaption && (
                  <p
                    data-testid={`service-caption-${s.key}`}
                    className="mt-4 text-[11px] tracking-[0.26em] uppercase text-[#7A6B5F] italic"
                  >
                    {s.imageCaption}
                  </p>
                )}
              </div>
              <div className={`lg:col-span-6 ${idx % 2 === 0 ? "" : "lg:order-1"}`}>
                <p className="font-serif-display text-6xl text-[#6E1F2C] mb-6">{s.number}</p>
                <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight leading-[1.05]">{s.title}</h2>
                <p className="mt-4 text-[#6E1F2C] text-base italic">{s.tagline}</p>
                <p className="mt-6 text-[#7A6B5F] leading-relaxed">{s.description}</p>
                <ul className="mt-10 space-y-4">
                  {s.points.map((p) => (
                    <li key={p} className="flex gap-4 items-start">
                      <Check size={16} className="mt-1 text-[#6E1F2C] shrink-0" />
                      <span className="text-[#1C110C] text-sm">{p}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={s.key === "curtains-soft" ? "/curtains-dubai" : `/services/${s.key}`}
                  data-testid={`service-cta-${s.key}`}
                  className="mt-12 inline-flex items-center gap-3 border border-[#6E1F2C]/35 text-[#1C110C] px-8 py-4 text-[11px] tracking-[0.28em] uppercase hover:bg-[#6E1F2C]/5 transition-colors"
                >
                  View {s.title.split(" — ")[0]}
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Specialty service pages — subtle internal linking, not aggressive */}
      <section
        data-testid="services-specialty-strip"
        className="bg-[#F2E9D8] border-t border-[#6E1F2C]/15 py-24 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="eyebrow mb-5">Specialty Pages</p>
          <h2 className="font-serif-display text-3xl md:text-5xl tracking-tight text-[#1C110C] max-w-3xl">
            Looking for something specific?
          </h2>
          <p className="mt-6 max-w-2xl text-[#3E2A22] leading-[1.8] text-base md:text-lg font-light">
            Dedicated pages with scope, process and FAQs for the briefs we&rsquo;re asked about most often.
          </p>

          <div className="mt-12 md:mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {[
              {
                to: "/kitchen-renovation-dubai",
                kicker: "Kitchen",
                title: "Kitchen Renovation Dubai",
                desc: "Bespoke cabinetry, marble countertops and integrated appliances — Bosch, Siemens, Gaggenau, Miele, Fhiaba.",
                testid: "specialty-card-kitchen",
              },
              {
                to: "/custom-wardrobes-dubai",
                kicker: "Wardrobes",
                title: "Custom Wardrobes & Walk-in Closets",
                desc: "Floor-to-ceiling joinery in veneer, lacquer and fluted glass with Blum, Hettich, Häfele and Salice hardware.",
                testid: "specialty-card-wardrobes",
              },
              {
                to: "/custom-joinery-dubai",
                kicker: "Joinery",
                title: "Custom Joinery & Bespoke Cabinetry",
                desc: "TV walls, library walls, panelling, headboards, vanities and built-in furniture — manufactured at our Sharjah atelier.",
                testid: "specialty-card-joinery",
              },
              {
                to: "/custom-sofas-dubai",
                kicker: "Upholstery",
                title: "Custom Sofas & Upholstery",
                desc: "Hand-built at our Sharjah atelier — kiln-dried frames, eight-way hand-tied suspension, your choice of fabric or leather.",
                testid: "specialty-card-sofas",
              },
            ].map((s) => (
              <Link
                key={s.to}
                to={s.to}
                data-testid={s.testid}
                className="group block bg-[#FAF5EC] border border-[#6E1F2C]/15 hover:border-[#6E1F2C]/45 p-8 md:p-10 transition-colors"
              >
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#D4A963]">
                  {s.kicker}
                </p>
                <h3 className="mt-4 font-serif-display text-xl md:text-2xl text-[#1C110C] leading-snug group-hover:text-[#6E1F2C] transition-colors">
                  {s.title}
                </h3>
                <p className="mt-4 text-sm md:text-base text-[#3E2A22] leading-[1.75] font-light">
                  {s.desc}
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-[#6E1F2C] border-b border-[#6E1F2C]/40 pb-1 group-hover:gap-4 transition-all">
                  Read More <span aria-hidden>→</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <AnswersSection
        testid="services-answers"
        eyebrow="Answers"
        heading="Curtains & soft furnishings — questions answered."
      />

      <QuickQuoteStrip
        testid="services-quick-quote"
        prefilled="Hi Time Furniture, I'd like a quote for my project. Details: "
        variant="light"
      />

      <section className="bg-[#FAF5EC] border-t border-[#6E1F2C]/15 py-28 md:py-40 lg:py-48 text-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <p className="eyebrow mb-5">Next Step</p>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-tight max-w-3xl mx-auto">
            Let's discuss what you want to build.
          </h2>
          <Link
            to="/contact"
            data-testid="services-bottom-cta"
            className="mt-12 inline-flex items-center gap-3 bg-[#6E1F2C] text-[#FAF5EC] px-10 py-5 text-[11px] tracking-[0.28em] uppercase hover:bg-[#56151F] transition-colors"
          >
            Request a Consultation
          </Link>
          <p className="mt-10 text-sm md:text-base text-[#7A6B5F]">
            Prefer to see the work first?{" "}
            <Link
              to="/portfolio"
              data-testid="services-link-portfolio"
              className="text-[#6E1F2C] underline underline-offset-4 hover:text-[#56151F]"
            >
              See completed projects
            </Link>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
